import { Navigate, useLocation, useParams } from "react-router";
import { ROUTES } from "../../../components/shared/constant";

/**
 * Redirects for the URLs the skyAgent / skyBrain split retired, and for
 * skyBrain's removed Session History.
 *
 * These exist because the old paths are in people's bookmarks and in links
 * already sent by email. Without them `/skyagent/memory` is not a 404 — it is
 * worse: AppLayout's route guard finds no nav entry behind the path and
 * silently bounces to Applicants, so an old link looks like it opened the
 * wrong page rather than a moved one.
 *
 * Each retired path is also listed on its surface's `legacyHrefs` in
 * `surfaces.ts`, which is what admits it through that same guard long enough
 * for the redirect below to run.
 */

/**
 * Rebuilds the target with the caller's instance still attached.
 *
 * The instance id rides in one of two places, and dropping it sends the viewer
 * to whichever instance was last cached rather than the one they were looking
 * at: as a trailing path segment (every route is declared
 * `<base>/:instanceid?`) or as an `instanceid` query parameter. Both are
 * carried across verbatim, along with any hash.
 */
function useRedirectTarget(to: string): string {
  const { instanceid } = useParams();
  const { search, hash } = useLocation();
  return `${to}${instanceid ? `/${instanceid}` : ""}${search}${hash}`;
}

function LegacyRedirect({ to }: { to: string }) {
  // `replace`, so Back returns to wherever the old link was followed from
  // rather than to the retired URL, which would redirect forward again.
  return (
    <Navigate
      to={useRedirectTarget(to)}
      replace
    />
  );
}

/** `/skyagent/admin` — the combined admin page, now four separate surfaces. */
export const SkyAgentAdminRedirect = () => <LegacyRedirect to={ROUTES.SKYAGENT_CONFIGURATION} />;

/** `/skyagent/memory` — memory moved to skyBrain. */
export const SkyAgentMemoryRedirect = () => <LegacyRedirect to={ROUTES.SKYBRAIN_MEMORY} />;

/** `/skyagent/capabilities` — "Capabilities" is now skyBrain's Skills. */
export const SkyAgentCapabilitiesRedirect = () => <LegacyRedirect to={ROUTES.SKYBRAIN_SKILLS} />;

/**
 * `/skybrain/session-history` — removed. It was a read-only copy of the
 * conversation list the Workspace already shows, where the same chats can be
 * searched, reopened and continued.
 */
export const SkyBrainSessionHistoryRedirect = () => <LegacyRedirect to={ROUTES.SKYAGENT} />;
